import { useMemo } from "react";

import { useInfiniteQuery } from "@tanstack/react-query";

import salesApiService from "app/pages/sales-intelligence/services/salesApiService";

import {
    SALESLOFT_CADENCE_VISIBILITY,
    SALESLOFT_CADENCES_PAGE_SIZE,
    type SalesloftCadenceVisibility,
} from "../constants/salesloftConstants";

import { salesloftQueryKeys } from "./queryKeyFactory";

type UseSalesloftCadencesQueryParams = {
    enabled?: boolean;
    name?: string;
    visibility?: SalesloftCadenceVisibility;
    includeSteps?: boolean;
};

export const useSalesloftCadencesQuery = ({
    enabled = true,
    name = "",
    visibility = SALESLOFT_CADENCE_VISIBILITY.all,
    includeSteps = true,
}: UseSalesloftCadencesQueryParams = {}) => {
    const trimmedName = name.trim();

    const query = useInfiniteQuery({
        queryKey: salesloftQueryKeys.cadences({ name: trimmedName, visibility }),
        queryFn: ({ pageParam }) =>
            salesApiService.salesloft.fetchCadences({
                page: pageParam,
                pageSize: SALESLOFT_CADENCES_PAGE_SIZE,
                visibility,
                name: trimmedName || undefined,
                includeSteps,
            }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const { nextPage } = lastPage.paging;
            return nextPage !== null && nextPage > 0 ? nextPage : undefined;
        },
        enabled,
    });

    const { data } = query;

    const cadences = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);

    return {
        ...query,
        data: cadences,
        cadences,
    };
};
